import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardKpiCard } from "@/components/dashboard/DashboardKpiCard";
import { ModuleConsoleFooter } from "@/components/composition/ModuleConsoleFooter";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { t } from "@/core/composition/lexicon";
import { loadHospitalStaff, staffFullName } from "@/core/rh/rh-api";
import type { HospitalStaffMember, StaffLoadSource } from "@/core/rh/types";
import { routes } from "@/router/all_routes";

function IconUsers() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function RhEmployeesPage() {
  const [staff, setStaff] = useState<HospitalStaffMember[]>([]);
  const [source, setSource] = useState<StaffLoadSource>("demo");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const result = await loadHospitalStaff();
      if (cancelled) return;
      setStaff(result.staff);
      setSource(result.source);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(
      (m) =>
        staffFullName(m).toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.department.toLowerCase().includes(q) ||
        m.position.toLowerCase().includes(q),
    );
  }, [staff, search]);

  const pagination = useClientPagination(filtered, [search], 25);
  const { pagedItems } = pagination;

  const activeCount = staff.filter((m) => m.status === "ACTIVE").length;
  const serviceCount = new Set(staff.map((m) => m.department)).size;

  return (
    <div className="ph-dashboard">
      <PageHeader
        title={t("rh.employees")}
        description={`Annuaire ${t("section.rh").toLowerCase()} — données RH via API, présentation Hospitol.`}
        actions={
          <Link to={routes.rhDepartments} className="btn btn-outline-secondary btn-sm">
            {t("rh.departments")}
          </Link>
        }
      />

      {source === "demo" && (
        <div className="alert alert-light border small mb-3">
          Jeu de démonstration hospitalier actif (API RH hors ligne ou droits{" "}
          <code>hr.employee.read</code> manquants).
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-6 col-xl-4">
          <DashboardKpiCard
            label={t("rh.employees")}
            value={String(staff.length)}
            hint={`${activeCount} actif(s)`}
            icon={<IconUsers />}
            accent="teal"
          />
        </div>
        <div className="col-6 col-xl-4">
          <DashboardKpiCard
            label={t("rh.departments")}
            value={String(serviceCount)}
            hint="Services représentés"
            icon={<IconUsers />}
            accent="violet"
          />
        </div>
      </div>

      <div className="ph-dash-card">
        <div className="d-flex flex-wrap gap-2 justify-content-between mb-3 ph-stock-list-head">
          <h3 className="h6 mb-0">Effectifs</h3>
          <input
            type="search"
            className="form-control form-control-sm w-100 w-md-auto"
            style={{ maxWidth: "16rem" }}
            placeholder="Nom, service, fonction…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {loading ? (
          <p className="text-muted small mb-0">Chargement…</p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Service</th>
                  <th>Fonction</th>
                  <th>Email</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((member) => (
                  <tr key={member.id} className="ph-diag-row">
                    <td data-label="Nom" className="fw-medium">
                      {staffFullName(member)}
                    </td>
                    <td data-label="Service">{member.department}</td>
                    <td data-label="Fonction" className="small">
                      {member.position}
                    </td>
                    <td data-label="Email" className="small text-muted">
                      {member.email}
                    </td>
                    <td data-label="Statut">
                      <span
                        className={`badge ${member.status === "ACTIVE" ? "bg-success" : "bg-secondary"}`}
                      >
                        {member.status === "ACTIVE" ? "Actif" : member.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>

      <ModuleConsoleFooter consoleRoute={routes.rhAdminConsole} moduleLabel="ITaaS RH" />
    </div>
  );
}
